import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Container, makeStyles, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { Footer } from '../components/Footer';

// Importar la imagen FAQ
import faqImage from '../images/Faq/FAQ.png';

export const FaqScreen = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isZoomed, setIsZoomed] = useState(false);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [touchStartTime, setTouchStartTime] = useState(0);
  const [touchStartPos, setTouchStartPos] = useState({ x: 0, y: 0 });

  const handleImageContextMenu = event => {
    event.preventDefault();
  };

  const handleImageClick = () => {
    if (isMobile && !isDragging) {
      setIsZoomed(!isZoomed);
      if (!isZoomed) {
        setImagePosition({ x: 0, y: 0 });
      }
    }
  };

  const handleTouchStart = (e) => {
    if (isMobile) {
      const touch = e.touches[0];
      const now = Date.now();
      setTouchStartTime(now);
      const touchX = touch.clientX || touch.pageX;
      const touchY = touch.clientY || touch.pageY;
      setTouchStartPos({ x: touchX, y: touchY });

      if (isZoomed) {
        setIsDragging(true);
        setDragStart({
          x: touchX - imagePosition.x,
          y: touchY - imagePosition.y,
        });
      }
    }
  };

  const handleTouchMove = (e) => {
    if (isMobile) {
      // Si hay múltiples toques, es un gesto de pellizco - permitir zoom nativo
      if (e.touches.length > 1) {
        return; // No interferir con el zoom nativo
      }

      const touch = e.touches[0];
      const touchX = touch.clientX || touch.pageX;
      const touchY = touch.clientY || touch.pageY;
      const moveX = Math.abs(touchX - touchStartPos.x);
      const moveY = Math.abs(touchY - touchStartPos.y);

      // Si la imagen está zoomed y hay movimiento, arrastrar
      if (isZoomed && (moveX > 5 || moveY > 5)) {
        e.preventDefault();
        setIsDragging(true);
        const newX = touchX - dragStart.x;
        const newY = touchY - dragStart.y;

        // Limitar el movimiento para que la imagen no se salga demasiado
        const maxX = window.innerWidth * 0.3;
        const maxY = window.innerHeight * 0.3;

        setImagePosition({
          x: Math.max(-maxX, Math.min(maxX, newX)),
          y: Math.max(-maxY, Math.min(maxY, newY)),
        });
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (isMobile) {
      const timeDiff = Date.now() - touchStartTime;
      const touch = e.changedTouches[0];
      const touchX = touch.clientX || touch.pageX;
      const touchY = touch.clientY || touch.pageY;
      const moveX = Math.abs(touchX - touchStartPos.x);
      const moveY = Math.abs(touchY - touchStartPos.y);

      // Si fue un tap rápido sin movimiento significativo, hacer zoom
      if (!isZoomed && timeDiff < 300 && moveX < 10 && moveY < 10) {
        setIsZoomed(true);
        setImagePosition({ x: 0, y: 0 });
      } else if (isZoomed && timeDiff < 300 && moveX < 10 && moveY < 10 && !isDragging) {
        // Si está zoomed y fue un tap sin arrastrar, quitar zoom
        setIsZoomed(false);
        setImagePosition({ x: 0, y: 0 });
      }

      setIsDragging(false);
    }
  };

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h5" component="h1" className={classes.headerTitle}>
          Preguntas frecuentes acerca de TEscucha
        </Typography>
      </Box>

      <Box className={`${classes.imageWrapper} ${isZoomed ? 'wrapperZoomed' : ''}`}>
        <Box
          className={`${classes.imageContainer} ${isZoomed ? classes.imageZoomed : ''}`}
          onClick={handleImageClick}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={isZoomed && isMobile ? {
            transform: `translate(${imagePosition.x}px, ${imagePosition.y}px)`,
            transition: isDragging ? 'none' : 'transform 0.2s ease-out',
          } : {}}
        >
          <img
            src={faqImage}
            alt="Preguntas frecuentes acerca de TEscucha"
            className={`${classes.faqImage} ${isZoomed ? classes.faqImageZoomed : ''}`}
            onContextMenu={handleImageContextMenu}
            draggable={false}
            onError={e => {
              e.target.style.display = 'none';
              const errorDiv = document.createElement('div');
              errorDiv.textContent = 'Imagen no encontrada. Por favor, verifica que la imagen existe en la ruta especificada.';
              errorDiv.style.padding = '20px';
              errorDiv.style.textAlign = 'center';
              errorDiv.style.color = '#666';
              e.target.parentNode.appendChild(errorDiv);
            }}
          />
        </Box>
      </Box>

      <Box className={classes.footerContainer}>
        <Footer />
        <Link to="/" style={{ textDecoration: 'none' }}>
          <Button variant="outlined" color="primary" className={classes.backButton}>
            Regresar
          </Button>
        </Link>
      </Box>
    </Container>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1, '16px'),
    borderRadius: 4,
    marginBottom: theme.spacing(2),
  },
  headerTitle: {
    fontWeight: 'bold',
    textAlign: 'left',
  },
  imageWrapper: {
    backgroundColor: '#fff',
    padding: theme.spacing(3),
    borderRadius: 8,
    boxShadow: 'rgba(0, 0, 0, 0.05) 0px 1px 2px 0px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    overflow: 'hidden',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      minHeight: 'auto',
      overflow: 'hidden',
      WebkitOverflowScrolling: 'touch',
    },
    '&.wrapperZoomed': {
      [theme.breakpoints.down('sm')]: {
        overflow: 'auto',
      },
    },
  },
  imageContainer: {
    display: 'inline-block',
    width: 'auto',
    maxWidth: '100%',
    position: 'relative',
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
      touchAction: 'pan-x pan-y pinch-zoom',
    },
    '&.imageZoomed': {
      [theme.breakpoints.down('sm')]: {
        cursor: 'grab',
        '&:active': {
          cursor: 'grabbing',
        },
      },
    },
  },
  faqImage: {
    width: 'auto',
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
    userSelect: 'none',
    objectFit: 'contain',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: 'auto',
      maxHeight: 'calc(100vh - 200px)',
      objectFit: 'contain',
      touchAction: 'pan-x pan-y pinch-zoom',
    },
    '&.faqImageZoomed': {
      [theme.breakpoints.down('sm')]: {
        width: '200%',
        maxWidth: '200%',
        height: 'auto',
        maxHeight: 'none',
        transformOrigin: 'center center',
      },
    },
  },
  zoomHint: {
    position: 'absolute',
    bottom: theme.spacing(2),
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(1),
    pointerEvents: 'none',
    zIndex: 1,
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  zoomHintText: {
    fontSize: '0.75rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  footerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 0),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  backButton: {
    textTransform: 'none',
    borderWidth: 1,
    backgroundColor: '#fff',
    '&:hover': {
      borderWidth: 1,
    },
  },
}));

